import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import { fmtNum } from "../../lib/format";

// Глобальный watcher — крутится на уровне App, опрашивает /api/alerts/check
// раз в 30 секунд, если есть активные правила. Сработавшие сервер удаляет
// и возвращает их с текущей ценой; клиент показывает Notification и
// инвалидирует кэш списка алертов, чтобы UI обновился.
//
// Single-shot semantics: чтобы не спамить дубликатами при медленной
// доставке нотификации, мы проверяем permission один раз на старте и
// держим показанные id в Set, на случай если сервер вернёт одно и то же
// в гонке между двумя вкладками.
const POLL_MS = 30_000;
const shown = new Set<number>();

export function AlertWatcher() {
  const qc = useQueryClient();

  // Узнаём, есть ли вообще правила, чтобы не дёргать /check впустую.
  const list = useQuery({
    queryKey: ["alerts"],
    queryFn: async ({ signal }) => {
      const { data, error } = await api.GET("/api/alerts", { signal });
      if (error || !data) throw new Error(String(error ?? "no data"));
      return data;
    },
    staleTime: 30_000,
  });

  const hasActive = (list.data?.length ?? 0) > 0;

  // /api/alerts/check вызываем поллингом — он удаляет сработавшие правила
  // на стороне сервера, поэтому refetch на интервале и есть «опрос». При
  // отсутствии активных правил отключаем (enabled:false).
  const check = useQuery({
    queryKey: ["alerts", "check"],
    queryFn: async ({ signal }) => {
      const { data, error } = await api.POST("/api/alerts/check", { signal });
      if (error || !data) throw new Error(String(error ?? "no data"));
      return data;
    },
    refetchInterval: hasActive ? POLL_MS : false,
    refetchIntervalInBackground: false,
    enabled: hasActive,
    staleTime: 0,
  });

  useEffect(() => {
    const triggered = check.data;
    if (!triggered || triggered.length === 0) return;
    let fired = false;
    for (const t of triggered) {
      if (shown.has(t.rule.id)) continue;
      shown.add(t.rule.id);
      fired = true;
      const dir = t.rule.kind === "price_below" ? "↓ ниже" : "↑ выше";
      const title = `Алерт: ${t.secname ?? t.rule.secid}`;
      const body =
        `Цена ${dir} ${fmtNum(t.rule.threshold)} ₽. Сейчас ${fmtNum(t.current_price)} ₽.`;
      tryNotify(title, body);
    }
    if (fired) {
      // список изменился — обновим кэш, чтобы пилюля счётчика на UI слезла
      qc.invalidateQueries({ queryKey: ["alerts"] });
    }
  }, [check.data, qc]);

  return null;
}

function tryNotify(title: string, body: string): void {
  if (!("Notification" in window)) return;
  if (Notification.permission === "granted") {
    new Notification(title, { body });
    return;
  }
  if (Notification.permission !== "denied") {
    Notification.requestPermission().then((p) => {
      if (p === "granted") new Notification(title, { body });
    });
  }
}
